// SPDX-FileCopyrightText: © 2024 Schneider Electric
//
// SPDX-License-Identifier: Apache-2.0

import type { ComponentInterface, EventEmitter } from '@stencil/core'
import {
  AttachInternals,
  Component,
  Element,
  Event,
  h,
  Host,
  Listen,
  Prop,
  State,
  Watch,
} from '@stencil/core'

import type { Attributes, QdsFocusEventDetail } from '../../helpers'
import { inheritAriaAttributes, pickFocusEventAttributes } from '../../helpers'
import type { Value } from '../controls'
import type { Size } from '../shared'

export type ChipType = 'closable' | 'selectable'

/**
 * `<qds-chip>` elements are interactive elements that represent selections.
 * Chips allow users to make a selection from a list of options or display
 * items like filters or tags.
 *
 * @see https://quartz.se.com/build/components/chip
 */
@Component({
  tag: 'qds-chip',
  formAssociated: true,
  shadow: { delegatesFocus: true },
  styleUrl: 'chip.css',
})
export class Chip implements ComponentInterface {
  /**
   * The name of the icon to render.
   *
   * Available names depend on the icon library being used.
   */
  @Prop() public readonly iconName?: string

  /**
   * The name of a registered icon library.
   */
  @Prop() public readonly iconLibrary: string = 'default'

  /**
   * The chip's size.
   */
  @Prop() public readonly size?: Size = 'standard'

  /**
   * The chip's type.
   */
  @Prop() public readonly type?: ChipType = 'selectable'

  /**
   * The chip's text.
   */
  @Prop() public readonly text?: string

  /**
   * Sets the selectable chip's state.
   *
   * @webnative
   */
  @Prop({ mutable: true }) public checked?: boolean

  /**
   * The [`<form>`](https://developer.mozilla.org/docs/Web/HTML/Element/form)
   * element to associate the chip with (its form owner).
   *
   * The value of this property must be the id of a `<form>` in the same
   * document. If this property is not set, the `<qds-chip>` is associated
   * with its ancestor `<form>` element, if any.
   *
   * This property lets you associate `<qds-chip>` elements to `<form>`s
   * anywhere in the document, not just inside a `<form>`. It can also override
   *an ancestor `<form>` element.
   *
   * @webnative
   */
  // eslint-disable-next-line unicorn/no-null
  @Prop() public readonly form: ElementInternals['form'] | string = null

  /**
   * Prevents the chip from being interacted with: it cannot be pressed or
   * focused.
   *
   */
  @Prop() public readonly disabled?: boolean

  /**
   * The name of the chip, which is submitted with the form data.
   *
   * @webnative
   */
  @Prop() public readonly name?: string

  /**
   * Specify whether the chip should have focus when the page loads.
   *
   * @webnative
   */
  @Prop({ attribute: 'autofocus' }) public readonly autoFocus?: boolean

  /**
   * The value of the chip, submitted as a name/value pair with form data.
   *
   * @webnative
   */
  @Prop() public readonly value?: Value = 'on'

  /**
   * Emitted when the chip loses focus.
   */
  @Event({ eventName: 'qdsBlur', bubbles: false, cancelable: false })
  private readonly blurEmitter!: EventEmitter<QdsFocusEventDetail>

  /**
   * Emitted when the chip gains focus.
   */
  @Event({ eventName: 'qdsFocus', bubbles: false, cancelable: false })
  private readonly focusEmitter!: EventEmitter<QdsFocusEventDetail>

  /**
   * Emitted when the chip is closed.
   */
  @Event({ eventName: 'qdsClose', cancelable: false })
  private readonly closeEmitter!: EventEmitter<void>

  /**
   * Emitted when a change to the selectable chip's state is committed by the user.
   */
  @Event({ eventName: 'qdsChange', cancelable: false })
  private readonly changeEmitter!: EventEmitter<void>

  @Element() private readonly host!: HTMLQdsChipElement

  @AttachInternals() private readonly internals!: ElementInternals

  @State() private isPressed = false

  @State() private tabIndex?: number

  #inheritedAttributes: Attributes = {}

  get #ariaLabel(): string {
    return `Delete ${this.text} selection`
  }

  get #computedDisabled(): boolean {
    return this.disabled ?? false
  }

  get #computedSize(): Size {
    switch (this.size) {
      case 'standard':
      case 'small':
      case 'large': {
        return this.size
      }
      default: {
        return 'standard'
      }
    }
  }

  get #computedType(): ChipType {
    switch (this.type) {
      case 'selectable':
      case 'closable': {
        return this.type
      }
      default: {
        return 'selectable'
      }
    }
  }

  get #computedChecked(): boolean {
    return this.checked ?? false
  }

  get #computedValue(): string | null {
    // eslint-disable-next-line unicorn/no-null
    return this.value == null ? null : this.value.toString()
  }

  @Listen('click')
  protected onClick(event: MouseEvent): void {
    if (this.#computedDisabled) {
      event.stopImmediatePropagation()
      return
    }

    event.preventDefault()

    if (this.#computedType === 'selectable') {
      this.checked = !this.#computedChecked
      this.changeEmitter.emit()
    }
  }

  @Listen('keydown')
  protected handleKeyDown(event: KeyboardEvent): void {
    if (this.#computedDisabled) {
      event.stopImmediatePropagation()
      return
    }

    if (event.key === ' ') {
      this.isPressed = true
      event.preventDefault()
    } else if (event.key === 'Enter' && this.internals.form) {
      this.internals.form.requestSubmit()
      event.preventDefault()
    }
  }

  @Listen('keyup')
  protected handleKeyUp(event: KeyboardEvent): void {
    if (event.key !== ' ') return

    this.isPressed = false
    if (this.#computedType === 'closable') this.closeEmitter.emit()
    else {
      this.checked = !this.#computedChecked
      this.changeEmitter.emit()
    }
  }

  @Watch('checked')
  protected checkedChanged(): void {
    this.internals.setFormValue(
      this.#computedChecked && !this.#computedDisabled
        ? this.#computedValue
        : // eslint-disable-next-line unicorn/no-null
          null,
    )
  }

  @Watch('disabled')
  protected disabledChanged(): void {
    if (this.#computedChecked && !this.#computedDisabled)
      this.internals.setFormValue(this.#computedValue)
    // eslint-disable-next-line unicorn/no-null
    else this.internals.setFormValue(null)
  }

  @Watch('tabindex')
  protected tabindexChanged(newValue: string): void {
    const parsedValue = Number.parseInt(newValue, 10)
    this.tabIndex =
      parsedValue === 0 || Number.isNaN(parsedValue) ? undefined : parsedValue
  }

  @Watch('value')
  protected valueChanged(): void {
    if (this.#computedChecked && !this.#computedDisabled)
      this.internals.setFormValue(this.#computedValue)
  }

  public componentWillLoad(): void {
    this.valueChanged()

    Object.defineProperty(this.host, 'form', {
      enumerable: true,
      get: () => this.internals.form,
    })

    this.#inheritedAttributes = inheritAriaAttributes(this.host)
    this.tabindexChanged(this.host.getAttribute('tabindex') ?? '0')
  }

  public render() {
    return (
      <Host>
        {this.#computedType === 'closable' && (
          <div
            aria-disabled={this.#computedDisabled ? 'true' : undefined}
            class={{
              'qds-container': true,
              'qds-closable-chip': true,
              'qds-pressed-state': this.isPressed,
            }}
            data-size={this.#computedSize}
          >
            {this.iconName !== undefined && this.iconName !== '' && (
              <qds-icon
                aria-hidden="true"
                class="qds-icon"
                data-size={this.#computedSize}
                library={this.iconLibrary}
                name={this.iconName}
              />
            )}
            <span class="qds-text" data-size={this.#computedSize}>
              {this.text}
            </span>
            <button
              aria-label={this.#ariaLabel}
              class="qds-target"
              data-size={this.#computedSize}
              disabled={this.#computedDisabled}
              onClick={this.#onIconClick}
              tabIndex={this.#computedDisabled ? undefined : this.tabIndex}
              type="button"
              // eslint-disable-next-line react/jsx-props-no-spreading
              {...this.#inheritedAttributes}
            >
              <qds-icon
                aria-hidden="true"
                class="qds-icon"
                data-size={this.#computedSize}
                library="core"
                name="close"
              />
            </button>
          </div>
        )}
        {this.#computedType === 'selectable' && (
          // eslint-disable-next-line jsx-a11y/label-has-associated-control
          <label aria-disabled={this.#computedDisabled ? 'true' : undefined}>
            <input
              // eslint-disable-next-line jsx-a11y/no-autofocus
              autoFocus={this.autoFocus}
              checked={this.checked}
              class="qds-checkbox"
              data-size={this.#computedSize}
              disabled={this.#computedDisabled}
              name={this.name}
              onBlur={this.#onBlur}
              onFocus={this.#onFocus}
              tabIndex={this.#computedDisabled ? undefined : this.tabIndex}
              type="checkbox"
              value={this.value ?? undefined}
              // eslint-disable-next-line react/jsx-props-no-spreading
              {...this.#inheritedAttributes}
            />
            <div
              class={{
                'qds-container': true,
                'qds-pressed-state': this.isPressed,
                'qds-padding-end': true,
                'qds-cursor': true,
              }}
              data-size={this.#computedSize}
            >
              {this.iconName !== undefined && this.iconName !== '' && (
                <qds-icon
                  aria-hidden="true"
                  class="qds-icon"
                  data-size={this.#computedSize}
                  library={this.iconLibrary}
                  name={this.iconName}
                />
              )}
              <span class="qds-text" data-size={this.#computedSize}>
                {this.text}
              </span>
              {(this.checked ?? false) && (
                <qds-icon
                  aria-hidden="true"
                  class="qds-icon"
                  data-size={this.#computedSize}
                  library="core"
                  name="checked"
                />
              )}
            </div>
          </label>
        )}
      </Host>
    )
  }

  readonly #onIconClick = (event: MouseEvent): void => {
    if (!event.defaultPrevented && this.#computedType === 'closable')
      this.closeEmitter.emit()
  }

  readonly #onFocus = (event: FocusEvent): void => {
    this.focusEmitter.emit(pickFocusEventAttributes(event))
  }

  readonly #onBlur = (event: FocusEvent): void => {
    this.blurEmitter.emit(pickFocusEventAttributes(event))
  }
}
